import { zodResolver } from "@hookform/resolvers/zod";
import {
  type Account,
  type CostCenter,
  createAccountSchema,
  createCostCenterSchema,
  type Dimension,
  DIMENSIONS,
  updateAccountSchema,
  updateCostCenterSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Input,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ListTree, Pencil, Plus } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountTree } from "../../../features/accounting/account-tree.js";
import {
  buildAccountTree,
  isValidChildCode,
  searchAccounts,
  suggestChildCode,
} from "../../../features/accounting/chart-tree.js";
import { AccountingPage, companySearchShape } from "../../../features/accounting/company.js";
import { useCostCenters } from "../../../features/accounting/shared.js";
import { zodErrorMap } from "../../../features/accounting/validation.js";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(["accounts", "cost-centers"]).optional().catch(undefined),
  account: z.string().uuid().optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  inactive: z.enum(["true"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/hesap-plani")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ChartPage,
});

function ChartPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <AccountingPage title={t("menu.chartOfAccounts")} description={t("accounting.chart.description")}>
      {({ companyId }) => (
        <Tabs
          value={search.tab ?? "accounts"}
          onValueChange={(tab) =>
            void navigate({
              search: (previous) => ({
                company: previous.company,
                ...(tab === "cost-centers" ? { tab: "cost-centers" as const } : {}),
              }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.chartOfAccounts")}>
            <TabsTrigger value="accounts">{t("accounting.chart.tabs.accounts")}</TabsTrigger>
            <TabsTrigger value="cost-centers">{t("accounting.chart.tabs.costCenters")}</TabsTrigger>
          </TabsList>
          <TabsContent value="accounts">
            <Accounts companyId={companyId} />
          </TabsContent>
          <TabsContent value="cost-centers">
            <CostCenters companyId={companyId} />
          </TabsContent>
        </Tabs>
      )}
    </AccountingPage>
  );
}

function useAllAccounts(companyId: string) {
  return useQuery({
    queryKey: queryKeys.accounting(companyId, "accounts", { all: true, includeInactive: true }),
    queryFn: async () =>
      (await api.GET("/accounting/accounts", { params: { query: { companyId, includeInactive: "true" } } }))
        .data!.items,
  });
}

/** Hesap planı ağacı + seçili hesabın yan paneli. Arama ve seçim URL'de yaşar (paylaşılabilir bağlantı). */
function Accounts({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const accounts = useAllAccounts(companyId);
  const [searchText, setSearchText] = useState(search.q ?? "");
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const showInactive = search.inactive === "true";
  const patchSearch = (patch: Partial<typeof search>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) patchSearch({ q: searchText.trim() || undefined });
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const all = useMemo(() => accounts.data ?? [], [accounts.data]);
  const shown = useMemo(
    () => (showInactive ? all : all.filter((account) => account.isActive)),
    [all, showInactive],
  );
  const found = useMemo(() => searchAccounts(shown, search.q ?? ""), [shown, search.q]);
  const nodes = useMemo(
    () => buildAccountTree(found ? shown.filter((account) => found.visible.has(account.id)) : shown),
    [shown, found],
  );
  // Başlangıçta yalnızca hesap sınıfları açıktır; arama varken eşleşenlerin üst zinciri açılır.
  const defaultExpanded = useMemo(
    () => new Set(shown.filter((account) => account.level === 1).map((account) => account.id)),
    [shown],
  );
  const effectiveExpanded = useMemo(
    () => (found ? new Set([...(expanded ?? []), ...found.expanded]) : (expanded ?? defaultExpanded)),
    [found, expanded, defaultExpanded],
  );
  const selected = all.find((account) => account.id === search.account) ?? null;

  if (accounts.error) return <Alert tone="danger">{errorMessage(accounts.error)}</Alert>;
  if (!accounts.data) return <SkeletonRows rows={10} />;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
      <Card className="p-3">
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <FormField label={t("common.search")} className="min-w-52 flex-1">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t("accounting.chart.searchPlaceholder")}
              />
            )}
          </FormField>
          <label className="flex h-10 items-center gap-2 text-sm">
            <Checkbox
              checked={showInactive}
              onChange={(event) => patchSearch({ inactive: event.target.checked ? "true" : undefined })}
            />
            {t("accounting.chart.showInactive")}
          </label>
        </div>
        {found ? (
          <p role="status" className="mb-2 px-1 text-xs text-muted-foreground">
            {t("accounting.chart.matchCount", { count: found.matches.size })}
          </p>
        ) : null}
        {nodes.length === 0 ? (
          <EmptyState
            icon={<ListTree />}
            title={t("accounting.chart.emptySearch")}
            description={t("accounting.chart.emptySearchHint")}
          />
        ) : (
          <AccountTree
            label={t("menu.chartOfAccounts")}
            nodes={nodes}
            expanded={effectiveExpanded}
            onToggle={(id, open) =>
              setExpanded(() => {
                const next = new Set(effectiveExpanded);
                if (open) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            selectedId={selected?.id ?? null}
            onSelect={(id) => patchSearch({ account: id })}
            highlighted={found?.matches ?? null}
          />
        )}
      </Card>

      {selected ? (
        <AccountPanel
          key={`${selected.id}-${selected.version}`}
          account={selected}
          all={all}
          onCreated={(created) => {
            setExpanded(new Set([...effectiveExpanded, selected.id]));
            patchSearch({ account: created.id });
          }}
        />
      ) : (
        <Card className="p-5 text-sm text-muted-foreground">{t("accounting.chart.selectHint")}</Card>
      )}
    </div>
  );
}

function AccountPanel({
  account,
  all,
  onCreated,
}: {
  account: Account;
  all: readonly Account[];
  onCreated: (account: Account) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermissions().has("accounting.chart.manage");
  const [name, setName] = useState(account.name);
  const [dimensions, setDimensions] = useState<Dimension[]>(account.requiredDimensions);
  const [nameError, setNameError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const usage = useQuery({
    queryKey: queryKeys.accounting(account.companyId, "account-usage", account.id),
    queryFn: async () =>
      (await api.GET("/accounting/accounts/{id}/usage", { params: { path: { id: account.id } } })).data!,
  });
  const postingKeys = useQuery({
    queryKey: queryKeys.accounting(account.companyId, "posting-keys"),
    staleTime: 3_600_000,
    queryFn: async () => (await api.GET("/accounting/posting-keys")).data!.items,
  });
  const keyLabel = (key: string) => postingKeys.data?.find((item) => item.key === key)?.label ?? key;

  const update = useMutation({
    mutationFn: async (patch: { name?: string; requiredDimensions?: Dimension[]; isActive?: boolean }) =>
      (
        await api.PATCH("/accounting/accounts/{id}", {
          params: { path: { id: account.id } },
          body: updateAccountSchema.parse({ ...patch, version: account.version }),
        })
      ).data!,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(account.companyId, "accounts") });
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const changed =
    name.trim() !== account.name ||
    [...dimensions].sort().join() !== [...account.requiredDimensions].sort().join();
  const facts: Array<[string, string]> = [
    [t("accounting.chart.fields.type"), t(`enums.accountType.${account.accountType}`)],
    [t("accounting.chart.fields.normalBalance"), t(`enums.balanceSide.${account.normalBalance}`)],
    [t("accounting.chart.fields.level"), String(account.level)],
    [
      t("accounting.chart.fields.postable"),
      account.isPostable ? t("accounting.chart.postable") : t("accounting.chart.notPostable"),
    ],
  ];

  return (
    <Card className="grid gap-5 p-5 lg:sticky lg:top-20">
      <header className="grid gap-1">
        <h2 className="text-lg font-semibold">
          <span className="font-mono">{account.code}</span> {account.name}
        </h2>
        <div>
          <StatusBadge tone={account.isActive ? "success" : "neutral"}>
            {account.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        </div>
      </header>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>

      <form
        className="grid gap-3 border-t pt-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === "") return setNameError(t("common.required"));
          setNameError(null);
          update.mutate({ name: name.trim(), requiredDimensions: dimensions });
        }}
      >
        <FormField label={t("accounting.chart.fields.name")} error={nameError ?? undefined} required>
          {(control) => (
            <Input
              {...control}
              value={name}
              maxLength={200}
              disabled={!canManage}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </FormField>
        <fieldset className="grid gap-1.5" disabled={!canManage}>
          <legend className="mb-1 text-sm font-medium">{t("accounting.chart.requiredDimensions")}</legend>
          {DIMENSIONS.map((dimension) => (
            <label key={dimension} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={dimensions.includes(dimension)}
                onChange={(event) =>
                  setDimensions((current) =>
                    event.target.checked
                      ? [...current, dimension]
                      : current.filter((item) => item !== dimension),
                  )
                }
              />
              {t(`accounting.dimensions.${dimension}`)}
            </label>
          ))}
          <p className="text-xs text-muted-foreground">{t("accounting.chart.requiredDimensionsHint")}</p>
        </fieldset>
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" loading={update.isPending} disabled={!changed}>
              {t("common.save")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ isActive: !account.isActive })}
            >
              {account.isActive ? t("common.deactivate") : t("common.activate")}
            </Button>
          </div>
        ) : null}
      </form>

      <section className="grid gap-2 border-t pt-4" aria-labelledby="account-usage-title">
        <h3 id="account-usage-title" className="text-sm font-semibold">
          {t("accounting.chart.usage.title")}
        </h3>
        {usage.error ? (
          <Alert tone="danger">{errorMessage(usage.error)}</Alert>
        ) : !usage.data ? (
          <SkeletonRows rows={2} />
        ) : (
          <>
            <p className="text-sm">
              {t("accounting.chart.usage.lines", { count: usage.data.lineCount })} ·{" "}
              {t("accounting.chart.usage.children", { count: usage.data.childCount })}
            </p>
            {usage.data.mappings.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("accounting.chart.usage.noMappings")}</p>
            ) : (
              <ul className="grid gap-1 text-sm" aria-label={t("accounting.chart.usage.mappings")}>
                {usage.data.mappings.map((mapping) => (
                  <li key={mapping.id} className="rounded-md bg-muted/50 px-2 py-1">
                    <span className="font-medium">{keyLabel(mapping.postingKey)}</span>{" "}
                    <span className="font-mono text-xs text-muted-foreground">
                      {[
                        mapping.postingKey,
                        mapping.keyParam,
                        mapping.businessUnit ? t(`enums.businessUnit.${mapping.businessUnit}`) : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {canManage ? (
        <div className="border-t pt-4">
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t("accounting.chart.child.action")}
          </Button>
        </div>
      ) : null}
      <Dialog
        open={adding}
        onOpenChange={setAdding}
        title={t("accounting.chart.child.title", { code: account.code })}
        description={t("accounting.chart.child.description")}
      >
        {adding ? (
          <ChildAccountForm
            parent={account}
            siblingCodes={all.filter((item) => item.parentId === account.id).map((item) => item.code)}
            hasLines={(usage.data?.lineCount ?? 0) > 0}
            onCancel={() => setAdding(false)}
            onCreated={(created) => {
              setAdding(false);
              onCreated(created);
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

type ChildValues = z.input<typeof createAccountSchema>;

function ChildAccountForm({
  parent,
  siblingCodes,
  hasLines,
  onCancel,
  onCreated,
}: {
  parent: Account;
  siblingCodes: readonly string[];
  hasLines: boolean;
  onCancel: () => void;
  onCreated: (account: Account) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<ChildValues, unknown, z.output<typeof createAccountSchema>>({
    resolver: zodResolver(createAccountSchema, { error: zodErrorMap }),
    defaultValues: {
      companyId: parent.companyId,
      parentId: parent.id,
      // Öneri: bir sonraki boş kod. Kullanıcı değiştirebilir; kural sunucuda da denetlenir.
      code: suggestChildCode(parent.code, siblingCodes) ?? "",
      name: "",
      requiredDimensions: parent.requiredDimensions,
    },
  });
  const { errors, isSubmitting } = form.formState;
  const dimensions = form.watch("requiredDimensions") ?? [];

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    if (!isValidChildCode(parent.code, values.code))
      return form.setError("code", { message: t("accounting.chart.child.codeRule", { code: parent.code }) });
    try {
      const created = (await api.POST("/accounting/accounts", { body: values })).data!;
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(parent.companyId, "accounts") });
      toast({ tone: "success", title: t("accounting.chart.child.created", { code: created.code }) });
      onCreated(created as Account);
    } catch (error) {
      const unmatched = applyFieldErrors(error, form.setError, ["code", "name", "requiredDimensions"]);
      if (unmatched.length > 0 || Object.keys(form.formState.errors).length === 0)
        setFormError(errorMessage(error));
    }
  });

  return (
    <form className="grid gap-4" noValidate onSubmit={(event) => void submit(event)}>
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      {hasLines ? (
        <Alert tone="danger">{t("accounting.chart.child.parentHasLines", { code: parent.code })}</Alert>
      ) : parent.isPostable ? (
        <Alert tone="warning">{t("accounting.chart.child.parentCloses", { code: parent.code })}</Alert>
      ) : null}
      <FormField
        label={t("accounting.chart.fields.code")}
        description={t("accounting.chart.child.codeHint", { code: parent.code })}
        error={errors.code?.message}
        required
      >
        {(control) => <Input {...control} {...form.register("code")} className="font-mono" autoFocus />}
      </FormField>
      <FormField label={t("accounting.chart.fields.name")} error={errors.name?.message} required>
        {(control) => <Input {...control} {...form.register("name")} maxLength={200} />}
      </FormField>
      <fieldset className="grid gap-1.5">
        <legend className="mb-1 text-sm font-medium">{t("accounting.chart.requiredDimensions")}</legend>
        {DIMENSIONS.map((dimension) => (
          <label key={dimension} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={dimensions.includes(dimension)}
              onChange={(event) =>
                form.setValue(
                  "requiredDimensions",
                  event.target.checked
                    ? [...dimensions, dimension]
                    : dimensions.filter((item) => item !== dimension),
                  { shouldDirty: true },
                )
              }
            />
            {t(`accounting.dimensions.${dimension}`)}
          </label>
        ))}
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={isSubmitting} disabled={hasLines}>
          {t("accounting.chart.child.submit")}
        </Button>
      </div>
    </form>
  );
}

type CostCenterValues = z.input<typeof createCostCenterSchema>;

function CostCenters({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const canManage = usePermissions().has("accounting.chart.manage");
  const centers = useCostCenters(companyId);
  const [dialog, setDialog] = useState<{ center: CostCenter | null } | null>(null);

  const columns = useMemo<DataTableColumn<CostCenter>[]>(
    () => [
      {
        id: "code",
        header: t("accounting.costCenters.code"),
        hideable: false,
        cell: (row) => <span className="font-mono font-medium">{row.code}</span>,
      },
      { id: "name", header: t("accounting.costCenters.name"), cell: (row) => row.name },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              align: "right" as const,
              hideable: false,
              cell: (row: CostCenter) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={t("accounting.costCenters.edit", { name: row.name })}
                  onClick={() => setDialog({ center: row })}
                >
                  <Pencil aria-hidden="true" />
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, canManage],
  );

  return (
    <>
      <DataTable
        caption={t("accounting.chart.tabs.costCenters")}
        columns={columns}
        rows={centers.data ?? []}
        rowKey={(row) => row.id}
        loading={centers.isPending}
        error={centers.error ? <Alert tone="danger">{errorMessage(centers.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            title={t("accounting.costCenters.empty")}
            description={t("accounting.costCenters.emptyHint")}
          />
        }
        toolbar={
          canManage ? (
            <Button size="sm" onClick={() => setDialog({ center: null })}>
              <Plus aria-hidden="true" />
              {t("accounting.costCenters.add")}
            </Button>
          ) : null
        }
      />
      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title={dialog?.center ? t("accounting.costCenters.editTitle") : t("accounting.costCenters.add")}
      >
        {dialog ? (
          <CostCenterForm
            key={dialog.center?.id ?? "new"}
            companyId={companyId}
            center={dialog.center}
            onDone={() => setDialog(null)}
          />
        ) : null}
      </Dialog>
    </>
  );
}

function CostCenterForm({
  companyId,
  center,
  onDone,
}: {
  companyId: string;
  center: CostCenter | null;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(center?.isActive ?? true);
  const form = useForm<CostCenterValues, unknown, z.output<typeof createCostCenterSchema>>({
    resolver: zodResolver(createCostCenterSchema, { error: zodErrorMap }),
    defaultValues: { companyId, code: center?.code ?? "", name: center?.name ?? "" },
  });
  const { errors, isSubmitting } = form.formState;

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      if (center)
        await api.PATCH("/accounting/cost-centers/{id}", {
          params: { path: { id: center.id } },
          body: updateCostCenterSchema.parse({ name: values.name, isActive, version: center.version }),
        });
      else await api.POST("/accounting/cost-centers", { body: values });
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId, "cost-centers") });
      toast({ tone: "success", title: t("common.saved") });
      onDone();
    } catch (error) {
      const unmatched = applyFieldErrors(error, form.setError, ["code", "name"]);
      if (unmatched.length > 0 || Object.keys(form.formState.errors).length === 0)
        setFormError(errorMessage(error));
    }
  });

  return (
    <form className="grid gap-4" noValidate onSubmit={(event) => void submit(event)}>
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      <FormField
        label={t("accounting.costCenters.code")}
        description={center ? t("accounting.costCenters.codeLocked") : t("accounting.costCenters.codeHint")}
        error={errors.code?.message}
        required
      >
        {(control) => (
          <Input
            {...control}
            {...form.register("code")}
            className="font-mono uppercase"
            disabled={center !== null}
            autoFocus={center === null}
          />
        )}
      </FormField>
      <FormField label={t("accounting.costCenters.name")} error={errors.name?.message} required>
        {(control) => <Input {...control} {...form.register("name")} maxLength={200} />}
      </FormField>
      {center ? (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          {t("common.active")}
        </label>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
